<template>
  <div class="monthly-card-page" :dir="isHebrew ? 'rtl' : 'ltr'">
    <SingleRideHeader :presentedText="t('monthlyCardSetupTitle')" />

    <div class="monthly-card-content after_page_header">
      <b-container>

        <!-- Not logged in -->
        <div v-if="!isAppLoggedIn" class="auth-gate text-center">
          <i class="fas fa-user-lock auth-gate-icon mb-3"></i>
          <p class="auth-gate-message">{{ t('monthlyCardSetupNotLoggedIn') }}</p>
          <b-button variant="primary" class="action-btn" @click="openLoginForm">{{ t('login') }}</b-button>
        </div>

        <!-- Logged in but not a ride manager -->
        <div v-else-if="!isUserManager" class="auth-gate text-center">
          <i class="fas fa-exclamation-circle auth-gate-icon mb-3"></i>
          <p class="auth-gate-message">{{ t('monthlyCardSetupNotManager') }}</p>
          <b-button variant="outline-secondary" class="action-btn" @click="appLogOut">{{ t('logout') }}</b-button>
        </div>

        <!-- Loading -->
        <div v-else-if="loading" class="text-center py-5">
          <b-spinner variant="primary" style="width:3rem;height:3rem;" />
        </div>

        <!-- Success -->
        <div v-else-if="successState && !isUpdatingCard" class="success-card text-center">
          <div class="success-icon mb-3">
            <i class="fas fa-check-circle"></i>
          </div>
          <h3 class="mb-2">{{ savedMessage }}</h3>
          <p v-if="cardLast4" class="card-digits mb-4" dir="ltr">****&nbsp;{{ cardLast4 }}</p>
          <b-button variant="primary" class="action-btn" @click="goHome">{{ backLabel }}</b-button>
          <b-button variant="outline-primary" class="action-btn" style="margin-inline-start: 16px;" @click="startUpdateCard">
            <i class="fas fa-sync-alt" style="margin-inline-end: 10px;"></i>{{ t('monthlyCardSetupUpdateCard') }}
          </b-button>
        </div>

        <!-- Form -->
        <div v-else class="row justify-content-center">
          <!-- Info column -->
          <div class="col-lg-5 mb-4 mb-lg-0">
            <div class="info-card">
              <div class="info-icon mb-3">
                <i class="fas fa-credit-card"></i>
              </div>
              <h2 class="mb-3">{{ title }}</h2>
              <p class="intro-text">{{ t('monthlyCardSetupIntro1') }}</p>
              <p class="intro-text">{{ t('monthlyCardSetupIntro2') }}</p>

              <b-alert v-if="!hasSumitConfig" variant="warning" show class="mt-3">
                {{ missingSumit }}
              </b-alert>

              <div v-if="hasSumitConfig" class="setup-hint mt-3" :dir="isHebrew ? 'rtl' : 'ltr'">
                <i class="fas fa-info-circle setup-hint-icon"></i>
                <span>{{ setupHint }}</span>
              </div>

              <div class="security-row mt-4">
                <span class="security-badge-item">
                  <i class="fas fa-lock"></i> {{ t('monthlyCardSetupSecurePayment') }}
                </span>
                <span class="security-badge-item">
                  <i class="fas fa-shield-alt"></i> SSL
                </span>
              </div>
            </div>
          </div>

          <!-- Form column -->
          <div v-if="hasSumitConfig" class="col-lg-7">
            <div class="form-card">
              <b-alert v-if="loadError" variant="danger" show class="mb-4">{{ loadError }}</b-alert>
              <b-alert v-if="paymentErrorMessage" variant="warning" show class="mb-4">{{ paymentErrorMessage }}</b-alert>

              <form
                id="sumit-monthly-card-form"
                ref="paymentForm"
                data-og="form"
                method="post"
                action="javascript:void(0);"
                @submit.prevent
              >
                <div class="form-group">
                  <label class="field-label">{{ labelName }}</label>
                  <input v-model="customer.name" type="text" class="form-control field-input" name="customer_name" required @input="clearPaymentError" />
                </div>
                <div class="form-group">
                  <label class="field-label">{{ labelPhone }}</label>
                  <input v-model="customer.phone" type="tel" class="form-control field-input" name="customer_phone" required @input="clearPaymentError" />
                </div>
                <div class="form-group">
                  <label class="field-label">{{ labelCard }}</label>
                  <input type="text" class="form-control field-input" name="cardNumber" autocomplete="cc-number" inputmode="numeric" maxlength="20" data-og="cardnumber" @input="onCardNumberInput" />
                  <p v-if="unsupportedCardWarning" class="unsupported-card-warning">⚠️ {{ unsupportedCardWarning }}</p>
                </div>
                <div class="expiry-row form-group">
                  <div class="expiry-field">
                    <label class="field-label d-block">{{ labelExpM }}</label>
                    <select class="form-control field-input expiry-select" name="cardExpiryMonth" autocomplete="cc-exp-month" data-og="expirationmonth">
                      <option value="" disabled selected>MM</option>
                      <option v-for="m in expiryMonths" :key="m.value" :value="m.value">{{ m.label }}</option>
                    </select>
                  </div>
                  <span class="expiry-sep">/</span>
                  <div class="expiry-field">
                    <label class="field-label d-block">{{ labelExpY }}</label>
                    <select class="form-control field-input expiry-select" name="cardExpiryYear" autocomplete="cc-exp-year" data-og="expirationyear">
                      <option value="" disabled selected>YYYY</option>
                      <option v-for="y in expiryYears" :key="y" :value="y">{{ y }}</option>
                    </select>
                  </div>
                </div>
                <div class="row">
                  <div class="col-md-6 form-group">
                    <label class="field-label">CVV</label>
                    <input type="text" class="form-control field-input" name="cardCvv" autocomplete="cc-csc" inputmode="numeric" maxlength="4" data-og="cvv" />
                  </div>
                  <div class="col-md-6 form-group">
                    <label class="field-label">{{ labelCitizen }}</label>
                    <input type="text" class="form-control field-input" data-og="citizenid" />
                  </div>
                </div>
                <div class="og-errors text-danger mb-3" />
                <button id="submit-monthly-card-btn" type="submit" class="submit-btn" :disabled="isProcessing || !sumitInitialized">
                  <i v-if="isProcessing" class="fas fa-spinner fa-spin mr-2"></i>
                  <i v-else class="fas fa-lock mr-2"></i>
                  {{ isUpdatingCard ? t('monthlyCardSetupSaveNewCard') : submitLabel }}
                </button>
                <b-button
                  v-if="isUpdatingCard"
                  variant="outline-secondary"
                  class="w-100 mt-2"
                  :disabled="isProcessing"
                  @click="cancelUpdate"
                >
                  {{ t('cancel') }}
                </b-button>
              </form>
            </div>
          </div>
        </div>

      </b-container>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import SingleRideHeader from '@/components/SingleRideHeader';
import billingApi from '@/api/billing';
import config from '@/common/config';
import { translationMixin } from '@/mixins/translationMixin';
import { isValidContactPhone } from '@/common/validateContactPhone';
import { INTERNAL_HOME_PATH } from '@/common/appHome';

export default {
  name: 'MonthlyBillingCardSetup',
  components: { SingleRideHeader },
  mixins: [translationMixin],
  data() {
    return {
      loading: true,
      loadError: null,
      hasSavedFromServer: false,
      cardLast4: null,
      setupAmountIls: 1,
      customer: { name: '', email: '', phone: '' },
      sumitInitialized: false,
      isProcessing: false,
      paymentErrorMessage: '',
      successState: false,
      cardNumberRaw: '',
      isUpdatingCard: false,
    };
  },
  computed: {
    ...mapGetters(['apptoken', 'isAppLoggedIn', 'isUserManager']),
    hasSumitConfig() {
      return (
        config.config.SUMIT.COMPANY_ID &&
        config.config.SUMIT.COMPANY_ID !== 'YourCompanyID' &&
        config.config.SUMIT.API_PUBLIC_KEY &&
        config.config.SUMIT.API_PUBLIC_KEY !== 'APIPublicKey'
      );
    },
    title() { return this.t('monthlyCardSetupTitle'); },
    setupHint() {
      const amount = this.$store.state.regionConfig.currency === 'USD' ? '$1' : '₪1';
      return this.t('monthlyCardSetupHint', { amount });
    },
    savedMessage() { return this.t('monthlyCardSetupSavedMessage'); },
    backLabel() {
      return this.$route.query.returnTo ? this.t('monthlyCardSetupBackToRide') : this.t('monthlyCardSetupBack');
    },
    labelName() { return this.t('monthlyCardSetupLabelName'); },
    labelPhone() { return this.t('monthlyCardSetupLabelPhone'); },
    labelCard() { return this.t('monthlyCardSetupLabelCard'); },
    labelExpM() { return this.t('monthlyCardSetupLabelExpM'); },
    labelExpY() { return this.t('monthlyCardSetupLabelExpY'); },
    labelCitizen() { return this.t('monthlyCardSetupLabelCitizen'); },
    expiryMonths() {
      const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return names.map((name, i) => {
        const val = String(i + 1).padStart(2, '0');
        return { value: val, label: `${val} – ${name}` };
      });
    },
    expiryYears() {
      const current = new Date().getFullYear();
      return Array.from({ length: 12 }, (_, i) => current + i);
    },
    unsupportedCardWarning() {
      const digits = this.cardNumberRaw.replace(/\D/g, '');
      if (digits.length < 2) return null;
      const prefix4 = parseInt(digits.substring(0, 4), 10);
      const prefix2 = parseInt(digits.substring(0, 2), 10);
      if (prefix2 === 34 || prefix2 === 37) return this.t('monthlyCardSetupUnsupportedAmex');
      if (prefix2 === 36 || prefix2 === 38 || (prefix4 >= 300 && prefix4 <= 305)) return this.t('monthlyCardSetupUnsupportedDiners');
      return null;
    },
    submitLabel() {
      return this.isProcessing ? this.t('monthlyCardSetupProcessing') : this.t('monthlyCardSetupSubmit');
    },
    missingSumit() { return this.t('monthlyCardSetupMissingSumit'); },
  },
  watch: {
    loading(val) {
      if (!val) {
        this.$nextTick(() => this.initializeSumit());
      }
    },
    isAppLoggedIn(val) {
      if (val && this.isUserManager) {
        this.prefillFromStore();
        this.loadStatus();
        this.loadSumitScript();
      }
    },
    isUserManager(val) {
      if (val && this.isAppLoggedIn) {
        this.prefillFromStore();
        this.loadStatus();
        this.loadSumitScript();
      }
    },
  },
  created() {
    if (this.isAppLoggedIn && this.isUserManager) {
      this.prefillFromStore();
    }
  },
  mounted() {
    if (this.isAppLoggedIn && this.isUserManager) {
      this.loadStatus();
      this.loadSumitScript();
    }
  },
  methods: {
    ...mapActions(['openLoginForm', 'appLogOut']),
    prefillFromStore() {
      const u = this.$store.state.user || {};
      if (u.email) this.customer.email = u.email;
      if (u.username) this.customer.name = u.username;
      if (u.phone) this.customer.phone = u.phone;
    },
    async loadStatus() {
      this.loading = true;
      this.loadError = null;
      try {
        const { data } = await billingApi.getMonthlyPaymentMethod(this.apptoken);
        this.hasSavedFromServer = !!data.has_saved_card;
        this.cardLast4 = data.card_last4 || null;
        if (typeof data.setup_charge_amount_ils === 'number') {
          this.setupAmountIls = data.setup_charge_amount_ils;
        }
        if (this.hasSavedFromServer) {
          this.successState = true;
        }
      } catch (e) {
        this.loadError = e.response?.data?.message || this.t('monthlyCardSetupErrorLoad');
      } finally {
        this.loading = false;
      }
    },
    goHome() {
      const returnTo = this.$route.query.returnTo;
      this.$router.push(returnTo || INTERNAL_HOME_PATH);
    },
    startUpdateCard() {
      this.isUpdatingCard = true;
      this.sumitInitialized = false;
      this.$nextTick(() => this.initializeSumit());
    },
    cancelUpdate() {
      this.isUpdatingCard = false;
      this.paymentErrorMessage = '';
    },
    onCardNumberInput(e) {
      this.cardNumberRaw = e.target.value;
      this.clearPaymentError();
    },
    clearPaymentError() {
      this.paymentErrorMessage = '';
      this.removeSumitTokenInputs();
    },
    removeSumitTokenInputs() {
      const form = this.$refs.paymentForm;
      if (!form) return;
      ['input[name="og-token"]', 'input[data-og="token"]', 'input[name="SingleUseToken"]'].forEach((sel) => {
        form.querySelectorAll(sel).forEach((node) => node.parentNode?.removeChild(node));
      });
    },
    loadSumitScript() {
      this.ensureJQuery().then(() => {
        if (document.querySelector(`script[src="${config.config.SUMIT.PAYMENTS_SCRIPT_URL}"]`)) {
          this.initializeSumit();
          return;
        }
        const script = document.createElement('script');
        script.src = config.config.SUMIT.PAYMENTS_SCRIPT_URL;
        script.onload = () => {
          this.initializeSumit();
        };
        script.onerror = () => {
          this.paymentErrorMessage = this.t('monthlyCardSetupErrorSumitLoad');
        };
        document.head.appendChild(script);
      });
    },
    ensureJQuery() {
      return new Promise((resolve) => {
        if (typeof window.jQuery !== 'undefined') {
          resolve();
          return;
        }
        const jqueryScript = document.createElement('script');
        jqueryScript.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
        jqueryScript.integrity = 'sha256-/xUj+3OJU5yExlq6GSYGSHk7tPXikynS7ogEvDej/m4=';
        jqueryScript.crossOrigin = 'anonymous';
        jqueryScript.onload = () => resolve();
        jqueryScript.onerror = () => resolve();
        document.head.appendChild(jqueryScript);
      });
    },
    initializeSumit() {
      if (typeof window.jQuery === 'undefined' || typeof window.OfficeGuy === 'undefined') {
        setTimeout(() => this.initializeSumit(), 100);
        return;
      }
      if (!this.hasSumitConfig) {
        return;
      }
      window.jQuery(() => {
        const $form = window.jQuery('#sumit-monthly-card-form');
        if (!$form.length) {
          return;
        }
        window.OfficeGuy.Payments.BindFormSubmit({
          CompanyID: config.config.SUMIT.COMPANY_ID,
          APIPublicKey: config.config.SUMIT.API_PUBLIC_KEY,
          FormSelector: '#sumit-monthly-card-form',
          ErrorsClass: '.og-errors',
          ResponseCallback: (response) => this.handleTokenResponse(response),
        });
        this.sumitInitialized = true;
      });
    },
    handleTokenResponse(response) {
      const ok =
        response &&
        (response.Status === 0 ||
          response.Status === '0' ||
          response.Status === 'Success (0)' ||
          response.Success === true ||
          (response.Data && response.Data.SingleUseToken));
      if (!ok) {
        this.paymentErrorMessage =
          (response && (response.UserErrorMessage || response.TechnicalErrorDetails || response.Message)) ||
          this.t('monthlyCardSetupErrorToken');
        return;
      }
      let token = null;
      if (response.Data && response.Data.SingleUseToken) token = response.Data.SingleUseToken;
      else if (response.SingleUseToken) token = response.SingleUseToken;
      if (!token && this.$refs.paymentForm) {
        const form = this.$refs.paymentForm;
        const inp =
          form.querySelector('input[name="og-token"]') ||
          form.querySelector('input[data-og="token"]') ||
          form.querySelector('input[name="SingleUseToken"]');
        if (inp && inp.value) token = inp.value;
      }
      if (!token) {
        this.paymentErrorMessage = this.t('monthlyCardSetupErrorMissingToken');
        return;
      }
      this.removeSumitTokenInputs();
      if (!this.validateLocal()) return;
      this.submitRegister(token.trim());
    },
    validateLocal() {
      const name = (this.customer.name || '').trim();
      if (!name) {
        this.paymentErrorMessage = this.t('monthlyCardSetupErrorName');
        return false;
      }
      if (!isValidContactPhone(this.customer.phone)) {
        this.paymentErrorMessage = this.t('monthlyCardSetupErrorPhone');
        return false;
      }
      return true;
    },
    async submitRegister(singleUseToken) {
      const btn = document.getElementById('submit-monthly-card-btn');
      this.isProcessing = true;
      if (btn) btn.disabled = true;
      try {
        const { data } = await billingApi.registerMonthlyPaymentMethod(this.apptoken, {
          singleUseToken,
          customer: {
            name: this.customer.name.trim(),
            email: this.customer.email.trim(),
            phone: this.customer.phone.trim(),
          },
        });
        if (data.ServerMassageCode === 'secsses' || data.has_saved_card) {
          this.successState = true;
          this.isUpdatingCard = false;
          this.cardLast4 = data.card_last4 || null;
          this.paymentErrorMessage = '';
        } else {
          this.paymentErrorMessage = data.message || this.t('monthlyCardSetupErrorFailed');
        }
      } catch (e) {
        const d = e.response?.data;
        this.paymentErrorMessage =
          d?.message || d?.error || e.message || this.t('monthlyCardSetupErrorRequest');
      } finally {
        this.isProcessing = false;
        if (btn) btn.disabled = false;
      }
    },
  },
};
</script>

<style scoped>
.monthly-card-page {
  min-height: 100vh;
  background: #f8f9fe;
  padding-top: 72px;
}

.monthly-card-content {
  padding: 40px 0 60px;
}

/* Info card (left column) */
.info-card {
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: #e8f4f8;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.info-icon i {
  font-size: 24px;
  color: #07A0C3;
}

.info-card h2 {
  font-size: 24px;
  font-weight: 700;
  color: #0d2f29;
}

.intro-text {
  font-size: 16px;
  color: #555;
  line-height: 1.7;
}

.setup-hint {
  font-size: 14px;
  color: #777;
  background: #f0f7ff;
  border-radius: 8px;
  padding: 12px 16px;
  line-height: 1.6;
  text-align: start;
}

.setup-hint-icon {
  float: inline-start;
  margin-inline-end: 8px;
  margin-top: 3px;
  color: #07A0C3;
}

.security-row {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.security-badge-item {
  font-size: 13px;
  color: #555;
  background: #f0f0f0;
  border-radius: 20px;
  padding: 5px 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.security-badge-item i {
  color: #07A0C3;
}

/* Form card (right column) */
.form-card {
  background: #fff;
  border-radius: 16px;
  padding: 36px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
}

.field-label {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
  display: block;
  text-align: start;
}

.field-input {
  font-size: 16px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid #dde2e8;
  padding: 8px 14px;
  transition: border-color 0.2s;
}

.field-input:focus {
  border-color: #07A0C3;
  box-shadow: 0 0 0 3px rgba(7, 160, 195, 0.12);
}

.expiry-row {
  display: flex;
  align-items: flex-end;
  gap: 0;
}

.expiry-field {
  flex: 1;
}

.unsupported-card-warning {
  margin-top: 6px;
  margin-bottom: 0;
  font-size: 13px;
  color: #c0392b;
  font-weight: 600;
  text-align: start;
}

.expiry-select {
  appearance: auto;
  cursor: pointer;
  padding-right: 8px;
}

.expiry-sep {
  font-size: 20px;
  color: #999;
  padding: 0 10px;
  padding-bottom: 10px;
}

.submit-btn {
  width: 100%;
  padding: 14px;
  font-size: 17px;
  font-weight: 600;
  background: #07A0C3;
  color: #fff;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  margin-top: 8px;
}

.submit-btn:hover:not(:disabled) {
  background: #0589a8;
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

/* Success state */
.success-card {
  background: #fff;
  border-radius: 16px;
  padding: 60px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
  max-width: 480px;
  margin: 0 auto;
}

.success-icon i {
  font-size: 64px;
  color: #28a745;
}

.success-card h3 {
  font-size: 22px;
  font-weight: 700;
  color: #0d2f29;
}

.card-digits {
  font-size: 20px;
  color: #555;
  letter-spacing: 2px;
}

.action-btn {
  font-size: 16px;
  padding: 10px 32px;
  border-radius: 8px;
}

/* Alerts */
.form-card .alert,
.info-card .alert {
  font-size: 15px;
  border-radius: 8px;
}

.og-errors {
  font-size: 14px;
  min-height: 20px;
}

.auth-gate {
  background: #fff;
  border-radius: 16px;
  padding: 60px 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.08);
  max-width: 480px;
  margin: 0 auto;
}

.auth-gate-icon {
  font-size: 56px;
  color: #07A0C3;
  display: block;
}

.auth-gate-message {
  font-size: 17px;
  color: #444;
  margin-bottom: 24px;
  line-height: 1.6;
}
</style>
